// The little date arithmetic the parser needs, on plain dates ("yyyy-MM-dd"). It is done in UTC on the date alone,
// so no time zone can shift a day; the kit does the same on local midnights with Foundation's Calendar.
// Kept private to the parser: days.ts is the core's general home for days.

import type { Day, Weekday } from "../types";

function asDay(date: Date): Day {
  const year = String(date.getUTCFullYear()).padStart(4, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}` as Day;
}

function parts(day: Day): { year: number; month: number; day: number } {
  const [year = "0", month = "1", date = "1"] = day.split("-");
  return { year: Number(year), month: Number(month), day: Number(date) };
}

function utc(year: number, month: number, day: number): Date {
  // Date.UTC treats years below 100 as 1900 + year; setUTCFullYear doesn't.
  const date = new Date(Date.UTC(2000, 0, 1));
  date.setUTCFullYear(year, month - 1, day);
  return date;
}

/**
 * The date of a year, month and day. Lenient as Foundation's Calendar is: the 31st of February is the 3rd of March
 * (the 2nd in a leap year), so "31.2." reads as the kit reads it.
 */
export function dayFrom(year: number, month: number, day: number): Day {
  return asDay(utc(year, month, day));
}

export function yearOf(day: Day): number {
  return parts(day).year;
}

export function addDays(day: Day, count: number): Day {
  const { year, month, day: date } = parts(day);
  return asDay(utc(year, month, date + count));
}

/** Months added as Calendar adds them: the 31st of January plus a month is the last day of February. */
export function addMonths(day: Day, count: number): Day {
  const { year, month, day: date } = parts(day);
  const first = utc(year, month + count, 1);
  const length = daysInMonth(first.getUTCFullYear(), first.getUTCMonth() + 1);
  return dayFrom(first.getUTCFullYear(), first.getUTCMonth() + 1, Math.min(date, length));
}

export function daysInMonth(year: number, month: number): number {
  return utc(year, month + 1, 0).getUTCDate();
}

/** 1 = Sunday … 7 = Saturday, as Foundation's Calendar counts. */
export function weekdayOf(day: Day): Weekday {
  const { year, month, day: date } = parts(day);
  return (utc(year, month, date).getUTCDay() + 1) as Weekday;
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
export function daysBetween(from: Day, to: Day): number {
  const a = parts(from);
  const b = parts(to);
  return Math.round((utc(b.year, b.month, b.day).getTime() - utc(a.year, a.month, a.day).getTime()) / 86_400_000);
}

export function startOfMonth(day: Day): Day {
  const { year, month } = parts(day);
  return dayFrom(year, month, 1);
}

/** A JavaScript Date at noon UTC of the day, for Intl formatting with timeZone "UTC". */
export function noonUTC(day: Day): Date {
  const { year, month, day: date } = parts(day);
  const noon = utc(year, month, date);
  noon.setUTCHours(12);
  return noon;
}
